import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal, flushSync } from "react-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import DeleteConfirmationModal from "../../utils/DeleteConfirmationModal";

const MAX_INDENT_DEPTH = 9;
const MAX_AGENTS_PER_SECTION = 8;

const MATCH_BADGES = {
  exact:                { label: "Exact",       className: "ev-message-card6 border-0 ev-xsmall-p-blue" },
  semantic:             { label: "AI",          className: "ev-message-card3" },
  covered:              { label: "Covered",     className: "ev-message-card3" },
  placeholder_resolved: { label: "Placeholder", className: "ev-message-card3" },
  new:                  { label: "New",         className: "ev-message-card3-green" },
  manual:               { label: "Manual",      className: "ev-message-card3" },
};

const defaultAgentFor = (heading) => ({
  agent_name: `Agent - ${heading}`,
  agent_context: "",
  agent_ordering_id: 1,
});

const foldText = (text) =>
  String(text ?? "").normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();

const normName = (text) => {
  let value = String(text ?? "").normalize("NFKC").trim();
  value = value.replace(/^[A-Z]?\d+(?:\.\d+)*[.)]?\s+/, "");
  value = foldText(value).replace(/[^\p{L}\p{N}_\s]/gu, " ");
  return value.replace(/\s+/g, " ").trim();
};

// ── Ordering-id helpers ───────────────────────────────────────────────────────
const idParts = (id) => String(id ?? "").trim().split(".").filter(Boolean);

const orderingDepth = (id) => idParts(id).length;

const parentOrderingId = (id) => {
  const parts = idParts(id);
  return parts.length > 1 ? parts.slice(0, -1).join(".") : "";
};

const compareOrderingIds = (a, b) => {
  const aStr = String(a ?? "").trim();
  const bStr = String(b ?? "").trim();
  const aIsSummary = aStr.toUpperCase().startsWith("S");
  const bIsSummary = bStr.toUpperCase().startsWith("S");
  if (aIsSummary !== bIsSummary) return aIsSummary ? -1 : 1;

  const aParts = idParts(aStr.replace(/^[Ss]/, ""));
  const bParts = idParts(bStr.replace(/^[Ss]/, ""));
  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aSeg = aParts[i];
    const bSeg = bParts[i];
    if (aSeg === undefined) return -1;
    if (bSeg === undefined) return 1;
    const aNum = Number(aSeg);
    const bNum = Number(bSeg);
    if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
      if (aNum !== bNum) return aNum - bNum;
    } else if (aSeg !== bSeg) {
      return aSeg < bSeg ? -1 : 1;
    }
  }
  return 0;
};

const isSummaryOrderingId = (id) => /^[Ss]/.test(String(id ?? "").trim());

const isSummarySection = (section) =>
  section?.is_summary_section === true ||
  isSummaryOrderingId(section?.section_ordering_id);

const matchedSectionToList = (matched) => {
  if (!matched) return [];
  return Array.isArray(matched) ? matched : [matched];
};

const isPlaceholderSection = (section) =>
  section?.is_placeholder === true || section?.isPlaceholder === true;

const buildTemplateModel = (sections) => {
  const byOrder = {};
  const byName = {};
  const records = sections.map((section, index) => {
    const order = String(section.section_ordering_id ?? "").trim();
    const name = String(section.section_name ?? "").trim();
    if (order) byOrder[order] = index;
    if (name) {
      const normalised = normName(name);
      if (!byName[normalised]) byName[normalised] = [];
      byName[normalised].push(index);
    }
    return {
      index,
      key: section.__key,
      section,
      name,
      order,
      parentIndex: null,
      depth: 1,
      isPlaceholder: isPlaceholderSection(section),
    };
  });

  records.forEach((record) => {
    const rawParent = record.section.parent_section_id;
    const parentText =
      rawParent === null || rawParent === undefined ? "" : String(rawParent).trim();
    let parentIndex = null;

    if (parentText) {
      const nameHits = byName[normName(parentText)] || [];
      const priorHits = nameHits.filter((i) => i < record.index);
      const prefix = parentOrderingId(record.order);
      const prefixHit = nameHits.length > 1 && prefix
        ? nameHits.find((i) => i !== record.index && records[i].order === prefix)
        : undefined;
      if (prefixHit !== undefined) {
        parentIndex = prefixHit;
      } else if (priorHits.length > 0) {
        parentIndex = priorHits[priorHits.length - 1];
      } else if (byOrder[parentText] !== undefined) {
        parentIndex = byOrder[parentText];
      }
    }
    if (parentIndex === null && record.order.includes(".")) {
      const prefix = parentOrderingId(record.order);
      if (byOrder[prefix] !== undefined) parentIndex = byOrder[prefix];
    }
    if (parentIndex !== record.index) record.parentIndex = parentIndex;
  });

  records.forEach((record) => {
    let depth = 1;
    let parent = record.parentIndex;
    const seen = new Set();
    while (parent !== null && parent !== undefined && !seen.has(parent)) {
      seen.add(parent);
      depth += 1;
      parent = records[parent].parentIndex;
    }
    record.depth = depth;
  });

  const childrenByIndex = {};
  records.forEach((record) => {
    const parent = record.parentIndex === null ? "root" : record.parentIndex;
    if (!childrenByIndex[parent]) childrenByIndex[parent] = [];
    childrenByIndex[parent].push(record.index);
  });

  Object.keys(childrenByIndex).forEach((parent) => {
    childrenByIndex[parent].sort((a, b) =>
      compareOrderingIds(records[a].order, records[b].order)
    );
  });

  const recordByKey = {};
  records.forEach((record) => { recordByKey[record.key] = record; });

  return { records, childrenByIndex, recordByKey };
};

const resolveParentReferenceMode = (sections) => {
  const names = new Set();
  const orders = new Set();
  sections.forEach((section) => {
    const name = String(section.section_name ?? "").trim();
    const order = String(section.section_ordering_id ?? "").trim();
    if (name) names.add(normName(name));
    if (order) orders.add(order);
  });

  let nameRefs = 0;
  let orderRefs = 0;
  sections.forEach((section) => {
    const raw = section.parent_section_id;
    if (raw === null || raw === undefined || raw === "") return;
    const text = String(raw).trim();
    if (names.has(normName(text))) nameRefs += 1;
    if (orders.has(text)) orderRefs += 1;
  });
  return nameRefs >= orderRefs ? "name" : "ordering_id";
};

const buildDossierModel = (dossierTree) => {
  const byIndex = {};
  const childrenByIndex = {};
  const siblingIndexByIndex = {};
  const depthByIndex = {};
  const siblingCounts = {};

  (dossierTree || []).forEach((node) => { byIndex[node.index] = node; });

  (dossierTree || []).forEach((node) => {
    const parent = node.parent_index === null || node.parent_index === undefined
      ? "root"
      : node.parent_index;
    if (!childrenByIndex[parent]) childrenByIndex[parent] = [];
    childrenByIndex[parent].push(node.index);
    siblingCounts[parent] = (siblingCounts[parent] || 0) + 1;
    siblingIndexByIndex[node.index] = siblingCounts[parent];
  });

  (dossierTree || []).forEach((node) => {
    let depth = 1;
    let parent = node.parent_index;
    const seen = new Set();
    while (parent !== null && parent !== undefined && !seen.has(parent)) {
      seen.add(parent);
      depth += 1;
      parent = byIndex[parent]?.parent_index;
    }
    depthByIndex[node.index] = depth;
  });

  return { byIndex, childrenByIndex, siblingIndexByIndex, depthByIndex };
};

// ── Structure editing (reorder + level change) ────────────────────────────────

const childKeyMap = (model) => {
  const map = {};
  Object.keys(model.childrenByIndex).forEach((bucket) => {
    const parentKey =
      bucket === "root" ? "root" : model.records[Number(bucket)].key;
    map[parentKey] = model.childrenByIndex[bucket].map(
      (index) => model.records[index].key
    );
  });
  return map;
};

// Rewrites one section's ordering id and, recursively, its whole subtree's.
const rewriteOrderingIds = (ctx, key, newOrder, parentOrder) => {
  const section = ctx.byKey[key];
  if (!section) return;
  const oldOrder = String(section.section_ordering_id ?? "").trim();

  if (ctx.parentMode === "ordering_id" && parentOrder) {
    const raw = section.parent_section_id;
    const current = raw === null || raw === undefined ? "" : String(raw).trim();
    if (current && current !== parentOrder) section.parent_section_id = parentOrder;
  }

  if (newOrder === oldOrder) return;
  section.section_ordering_id = newOrder;

  let position = 0;
  (ctx.childKeysByParent[key] || []).forEach((childKey) => {
    const child = ctx.byKey[childKey];
    if (!child) return;
    position += 1;
    rewriteOrderingIds(ctx, childKey, `${newOrder}.${position}`, newOrder);
  });
};

const renumberSiblings = (ctx, parentKey) => {
  const parentSection = parentKey === "root" ? null : ctx.byKey[parentKey];
  const parentOrder = parentSection
    ? String(parentSection.section_ordering_id ?? "").trim()
    : "";
  if (!parentOrder) return;

  let position = 0;
  (ctx.childKeysByParent[parentKey] || []).forEach((childKey) => {
    const child = ctx.byKey[childKey];
    if (!child) return;
    position += 1;
    rewriteOrderingIds(ctx, childKey, `${parentOrder}.${position}`, parentOrder);
  });
};

const isSafeRewrite = (before, after) => {
  if (!after || after.length !== before.length) return false;

  const seenKeys = new Set();
  const seenOrders = new Set();
  for (const section of after) {
    if (seenKeys.has(section.__key)) return false;
    seenKeys.add(section.__key);
    const order = String(section.section_ordering_id ?? "").trim();
    if (!order) continue;
    if (seenOrders.has(order)) return false;
    if (orderingDepth(order) > MAX_INDENT_DEPTH) return false;
    seenOrders.add(order);
  }

  const fingerprint = (sections, pick) =>
    sections.map((section) => `${section.__key}\u0000${pick(section)}`).join("\u0001");

  if (
    fingerprint(before, (s) => s.section_name ?? "") !==
    fingerprint(after, (s) => s.section_name ?? "")
  ) return false;

  return true;
};

const subtreeIndices = (model, rootIndex) => {
  const out = [];
  const walk = (index) => {
    (model.childrenByIndex[index] || []).forEach((childIndex) => {
      out.push(childIndex);
      walk(childIndex);
    });
  };
  walk(rootIndex);
  return out;
};

const renameStealsChildren = (model, record, newName) => {
  const target = normName(newName);
  if (!target || target === normName(record.name)) return false;
  return subtreeIndices(model, record.index).some(
    (index) => normName(model.records[index].name) === target
  );
};

const renameLooksLikeOrderingId = (model, record, newName) => {
  const target = normName(newName);
  if (!target || target === normName(record.name)) return false;
  return model.records.some(
    (other) => other.order && normName(other.order) === target
  );
};

const applyRename = (sections, model, key, rawName, parentMode = "name") => {
  const record = model.recordByKey[key];
  const name = String(rawName ?? "").trim();
  if (!record || !name || name === record.name) return null;
  if (parentMode === "name" && renameStealsChildren(model, record, name)) return null;
  if (renameLooksLikeOrderingId(model, record, name)) return null;

  const oldNorm = normName(record.name);
  const oldAgentName = defaultAgentFor(record.name).agent_name;
  const newAgentName = defaultAgentFor(name).agent_name;
  const childKeys = new Set(
    (model.childrenByIndex[record.index] || []).map(
      (index) => model.records[index].key
    )
  );

  return sections.map((section) => {
    if (section.__key === key) {
      const renamed = { ...section, section_name: name };
      if (Array.isArray(section.agents)) {
        renamed.agents = section.agents.map((agent) =>
          agent && String(agent.agent_name ?? "").trim() === oldAgentName
            ? { ...agent, agent_name: newAgentName }
            : agent
        );
      }
      return renamed;
    }
    if (!childKeys.has(section.__key)) return section;
    const raw = section.parent_section_id;
    const text = raw === null || raw === undefined ? "" : String(raw).trim();
    if (!text || normName(text) !== oldNorm) return section;
    return { ...section, parent_section_id: name };
  });
};

const normAgentName = (text) => foldText(text).replace(/\s+/g, " ").trim();

const renumberAgents = (agents) =>
  agents.map((agent, index) => ({ ...agent, agent_ordering_id: index + 1 }));

const isAgentEditable = (section, agent) =>
  !!section?.__aiAdded || !!agent?.__added;

const applyAgentChange = (sections, sectionKey, transform) =>
  sections.map((section) => {
    if (section.__key !== sectionKey) return section;
    const next = transform(
      Array.isArray(section.agents) ? section.agents : [], section
    );
    return next ? { ...section, agents: renumberAgents(next) } : section;
  });

const parseReferList = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
};

const pinReferAgents = (sections) => {
  const norm = (value) => String(value ?? "").trim().toLowerCase();
  const byOrder = {};
  const byName = {};
  const nameByKey = {};
  sections.forEach((section) => {
    const sid = String(section.section_ordering_id ?? "").trim();
    (section.agents || []).forEach((agent) => {
      nameByKey[agent.__agentKey] = agent.agent_name ?? "";
      byOrder[`${sid}:${String(agent.agent_ordering_id ?? "").trim()}`] = agent.__agentKey;
      const nameKey = `${sid}:${norm(agent.agent_name)}`;
      if (!byName[nameKey]) byName[nameKey] = [];
      byName[nameKey].push(agent.__agentKey);
    });
  });

  return sections.map((section) => {
    if (!Array.isArray(section.agents)) return section;
    let pinned = false;
    const agents = section.agents.map((agent) => {
      const refs = parseReferList(agent.refer_agents);
      if (!refs || refs.length === 0) return agent;
      pinned = true;
      const pins = refs.map((ref) => {
        const sid = String(ref?.section_ordering_id ?? "").trim();
        let key =
          ref?.agent_ordering_id !== null && ref?.agent_ordering_id !== undefined
            ? byOrder[`${sid}:${String(ref.agent_ordering_id).trim()}`]
            : undefined;
        if (!key && ref?.agent_name) {
          const hits = byName[`${sid}:${norm(ref.agent_name)}`] || [];
          if (hits.length === 1) key = hits[0];
        }
        return key ? { key, ref, name: nameByKey[key] } : { ref };
      });
      return { ...agent, __referPins: pins };
    });
    return pinned ? { ...section, agents } : section;
  });
};

const unpinReferAgents = (sections) => {
  const finalPosition = {};
  sections.forEach((section) => {
    (section.agents || []).forEach((agent, index) => {
      if (!agent.__agentKey) return;
      finalPosition[agent.__agentKey] = {
        section_ordering_id: section.section_ordering_id ?? null,
        // The import pipeline numbers agents by position — match it.
        agent_ordering_id: index + 1,
        agent_name: agent.agent_name ?? "",
      };
    });
  });

  return (agent) => {
    if (!agent.__referPins) return agent;
    const { __referPins, ...rest } = agent;
    return {
      ...rest,
      refer_agents: __referPins.map(({ key, ref, name }) => {
        if (!key) return ref;
        const target = finalPosition[key];
        return target
          ? { ...ref, ...target }
          : {
            ...ref,
            section_ordering_id: null,
            agent_ordering_id: null,
            agent_name: ref?.agent_name || name || "",
          };
      }),
    };
  };
};

const nextAgentName = (agents) => {
  const used = new Set(agents.map((agent) => normAgentName(agent.agent_name)));
  let position = agents.length + 1;
  while (used.has(normAgentName(`Agent ${position}`))) position += 1;
  return `Agent ${position}`;
};

const AgentNameField = ({ value, validate, onCommit, autoFocus }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  const issue = validate(draft);

  const handleCommit = () => {
    if (issue) {
      setDraft(value);            // discard an invalid draft rather than trap focus
      return;
    }
    const name = draft.trim();
    if (name !== value) onCommit(name);
  };

  return (
    <>
      <input
        type="text"
        autoFocus={autoFocus}
        className={`genbi-map-rename-input ev-xsmall-p-dark flex-grow-1${
          issue ? " is-invalid" : ""
        }`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.target.select()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setDraft(value);
          }
        }}
        onBlur={handleCommit}
      />
      {issue && (
        <i
          className="bi bi-exclamation-circle text-danger flex-shrink-0 avatar-tooltip"
          data-tooltip={issue}
        ></i>
      )}
    </>
  );
};

const applyStructureMove = (sections, model, parentMode, move) => {
  const record = model.recordByKey[move.key];
  if (!record) return null;

  const byKey = {};
  const next = sections.map((section) => {
    const copy = { ...section };
    byKey[copy.__key] = copy;
    return copy;
  });

  const childKeysByParent = childKeyMap(model);
  const ctx = { byKey, childKeysByParent, parentMode };

  const oldParentKey =
    record.parentIndex === null ? "root" : model.records[record.parentIndex].key;
  const newParentKey = move.targetParentKey;
  if (newParentKey !== "root" && !byKey[newParentKey]) return null;

  const remaining = (parentKey) =>
    (childKeysByParent[parentKey] || []).filter((key) => key !== move.key);

  const targetSiblings = remaining(newParentKey);
  const insertAt = Math.max(0, Math.min(move.targetIndex, targetSiblings.length));
  targetSiblings.splice(insertAt, 0, move.key);

  if (newParentKey === oldParentKey) {
    childKeysByParent[oldParentKey] = targetSiblings;
  } else {
    childKeysByParent[oldParentKey] = remaining(oldParentKey);
    childKeysByParent[newParentKey] = targetSiblings;

    const newParent = newParentKey === "root" ? null : byKey[newParentKey];
    byKey[move.key].parent_section_id = !newParent
      ? null
      : parentMode === "name"
        ? newParent.section_name
        : newParent.section_ordering_id;
  }

  renumberSiblings(ctx, oldParentKey);
  if (newParentKey !== oldParentKey) renumberSiblings(ctx, newParentKey);

  return isSafeRewrite(sections, next) ? next : null;
};

const deletionKeys = (model, key) => {
  const record = model.recordByKey[key];
  if (!record) return [];
  return [
    key,
    ...subtreeIndices(model, record.index).map((index) => model.records[index].key),
  ];
};

const applySectionDelete = (sections, model, parentMode, key) => {
  const record = model.recordByKey[key];
  if (!record) return null;

  const removed = new Set(deletionKeys(model, key));
  const remaining = sections.filter((section) => !removed.has(section.__key));
  if (remaining.length === sections.length) return null;

  const parentKey =
    record.parentIndex === null ? "root" : model.records[record.parentIndex].key;
  if (parentKey === "root") return remaining;   // top-level ids are never derived

  const nextModel = buildTemplateModel(remaining);
  const byKey = {};
  const renumbered = remaining.map((section) => {
    const copy = { ...section };
    byKey[copy.__key] = copy;
    return copy;
  });
  renumberSiblings(
    { byKey, childKeysByParent: childKeyMap(nextModel), parentMode }, parentKey
  );

  return isSafeRewrite(remaining, renumbered) ? renumbered : remaining;
};

const DRAG_TRANSLATE = /^translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)$/;

const rowNodeFor = (container, key) =>
  container?.querySelector(`[data-rfd-draggable-id="${key}"]`) ?? null;

const clampDragPreview = (style, snapshot, bounds) => {
  if (!snapshot?.isDragging || !style || !bounds) return style;

  const offset = DRAG_TRANSLATE.exec(String(style.transform ?? "").trim());
  if (!offset) return style;

  const top = Number(style.top);
  const y = Number(offset[2]);
  if (!Number.isFinite(top) || !Number.isFinite(y)) return style;

  const height = Number(style.height);
  const minY = bounds.top - top;
  const maxY = Number.isFinite(height)
    ? bounds.bottom - top - height
    : Number.POSITIVE_INFINITY;
  const clampedY =
    maxY >= minY ? Math.min(Math.max(y, minY), maxY) : minY;

  return { ...style, transform: `translate(0px, ${clampedY}px)` };
};

const GenbiHeadingMappingModal = ({
  isOpen,
  mappingData,
  docName,
  libraryTemplateName,
  onCancel,
  onConfirm,
  readOnly = false,
}) => {

  // ── Initial state derived from the API response ─────────────────────────────
  const initialState = useMemo(() => {
    const rawSections = mappingData?.amended_template?.sections || [];
    const links = {};
    const matchTypes = {};

    const sections = rawSections.map((section, index) => {
      const key = `sec_${index}`;
      const matches = matchedSectionToList(section.matched_section);
      matches.forEach((match) => {
        if (match?.dossier_index === null || match?.dossier_index === undefined) return;
        links[match.dossier_index] = key;
        matchTypes[match.dossier_index] = match.match_type || "exact";
      });
      const isAiAdded =
        section.AI_Modified === true &&
        !isPlaceholderSection(section) &&
        matches.some((m) => m?.match_type === "new" || m?.match_type === "manual");
      const next = { ...section, __key: key, __aiAdded: isAiAdded };
      if (Array.isArray(section.agents)) {
        next.agents = section.agents.map((agent, agentIndex) => ({
          ...agent,
          __agentKey: `${key}_ag_${agentIndex}`,
        }));
      }
      return next;
    });

    return {
      sections: pinReferAgents(sections),
      links,
      matchTypes,
      parentMode: resolveParentReferenceMode(rawSections),
    };
  }, [mappingData]);

  const [sections, setSections] = useState(initialState.sections);
  const [linksByDossier, setLinksByDossier] = useState(initialState.links);
  const [matchTypeByDossier, setMatchTypeByDossier] = useState(initialState.matchTypes);
  const [selection, setSelection] = useState(null);
  const [groupIndex, setGroupIndex] = useState(1);
  const [filterMode, setFilterMode] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [isGroupMenuOpen, setIsGroupMenuOpen] = useState(false);
  const [dragCollapsedKey, setDragCollapsedKey] = useState(null);
  const [collapsedSectionKeys, setCollapsedSectionKeys] = useState(() => new Set());
  const [editingKey, setEditingKey] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [expandedAgentsKey, setExpandedAgentsKey] = useState(null);
  const [lastAddedAgentKey, setLastAddedAgentKey] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [customTitleKeys, setCustomTitleKeys] = useState(() => new Set());
  const [undoAction, setUndoAction] = useState(null);
  const groupMenuRef = useRef(null);
  const agentSeqRef = useRef(0);
  const pendingRenameRef = useRef(null);
  const scrollRef = useRef(null);
  const dragBoundsRef = useRef(null);
  const dragRestoreRef = useRef(null);

  useEffect(() => {
    setSections(initialState.sections);
    setLinksByDossier(initialState.links);
    setMatchTypeByDossier(initialState.matchTypes);
    setSelection(null);
    setGroupIndex(0);
    setFilterMode("all");
    setSearchInput("");
    setIsGroupMenuOpen(false);
    setDragCollapsedKey(null);
    setCollapsedSectionKeys(new Set());
    setEditingKey(null);
    setDraftName("");
    setExpandedAgentsKey(null);
    setLastAddedAgentKey(null);
    setPendingDelete(null);
    setCustomTitleKeys(new Set());
    setUndoAction(null);
    pendingRenameRef.current = null;
    dragRestoreRef.current = null;
  }, [initialState]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelection(null);
        setIsGroupMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (!isGroupMenuOpen) return;
    const handleClickOutside = (e) => {
      if (groupMenuRef.current && !groupMenuRef.current.contains(e.target)) {
        setIsGroupMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isGroupMenuOpen]);

  const templateModel = useMemo(() => buildTemplateModel(sections), [sections]);
  const collapsibleSectionKeys = useMemo(
    () => templateModel.records
      .filter((record) => (templateModel.childrenByIndex[record.index] || []).length > 0)
      .map((record) => record.key),
    [templateModel]
  );
  const allSectionsCollapsed =
    collapsibleSectionKeys.length > 0 &&
    collapsibleSectionKeys.every((key) => collapsedSectionKeys.has(key));
  const dossierModel = useMemo(
    () => buildDossierModel(mappingData?.dossier_tree),
    [mappingData]
  );
  const dossierOrder = useMemo(
    () => (mappingData?.dossier_tree || []).map((node) => node.index),
    [mappingData]
  );

  const handleToggleSection = (key) => {
    setCollapsedSectionKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleToggleAllSections = () => {
    setCollapsedSectionKeys(
      allSectionsCollapsed ? new Set() : new Set(collapsibleSectionKeys)
    );
  };

  const dossiersBySection = useMemo(() => {
    const map = {};
    Object.keys(linksByDossier).forEach((dossierIndex) => {
      const key = linksByDossier[dossierIndex];
      if (!map[key]) map[key] = [];
      map[key].push(Number(dossierIndex));
    });
    Object.keys(map).forEach((key) => { map[key].sort((a, b) => a - b); });
    return map;
  }, [linksByDossier]);

  const initialDossiersBySection = useMemo(() => {
    const map = {};
    Object.keys(initialState.links).forEach((dossierIndex) => {
      const key = initialState.links[dossierIndex];
      if (!map[key]) map[key] = [];
      map[key].push(Number(dossierIndex));
    });
    return map;
  }, [initialState]);

  const placeholderNames = useMemo(() => {
    const map = {};
    initialState.sections.forEach((section) => {
      if (!isPlaceholderSection(section)) return;
      const name = section.placeholder_name || (section.AI_Modified ? "" : section.section_name);
      if (String(name ?? "").trim()) map[section.__key] = String(name).trim();
    });
    return map;
  }, [initialState]);

  // ── Tree traversal helpers ──────────────────────────────────────────────────
  const templateSubtree = (rootIndex) => subtreeIndices(templateModel, rootIndex);

  const dossierSubtree = (rootIndex) => {
    const out = [];
    const walk = (index) => {
      (dossierModel.childrenByIndex[index] || []).forEach((childIndex) => {
        out.push(childIndex);
        walk(childIndex);
      });
    };
    walk(rootIndex);
    return out.sort((a, b) => a - b);
  };

  const dossierRootOf = (index) => {
    let current = index;
    const seen = new Set();
    while (true) {
      const parentIndex = dossierModel.byIndex[current]?.parent_index;
      if (parentIndex === null || parentIndex === undefined || seen.has(parentIndex)) {
        return current;
      }
      seen.add(parentIndex);
      current = parentIndex;
    }
  };

  // ── Groups: one per top-level template section, plus an unassigned bucket ───
  const groups = useMemo(() => {
    const rootIndices = templateModel.childrenByIndex["root"] || [];
    const list = rootIndices.map((index) => {
      const record = templateModel.records[index];
      const subtreeKeys = [
        record.key,
        ...templateSubtree(index).map((i) => templateModel.records[i].key),
      ];
      const linkedDossierIndices = subtreeKeys.flatMap(
        (key) => dossiersBySection[key] || []
      );
      const affiliatedDossierIndices = subtreeKeys.flatMap(
        (key) => initialDossiersBySection[key] || []
      );
      const docRootIndices = [
        ...new Set(
          [...linkedDossierIndices, ...affiliatedDossierIndices].map((di) =>
            dossierRootOf(di)
          )
        ),
      ];
      const docRootIndex = docRootIndices.length > 0 ? docRootIndices[0] : null;
      return {
        key: record.key,
        rootIndex: index,
        rootKey: record.key,
        rootRecord: record,
        docRootIndex,
        docRootIndices,
        isUnassigned: false,
      };
    });

    // Add "All sections" view at the top
    list.unshift({
      key: "__all__",
      rootIndex: null,
      rootKey: null,
      rootRecord: null,
      docRootIndex: null,
      isAll: true,
      isUnassigned: false,
    });

    const claimed = new Set(list.filter((g) => g.docRootIndices).flatMap((g) => g.docRootIndices));
    const orphanRoots = (dossierModel.childrenByIndex["root"] || []).filter(
      (index) => !claimed.has(index)
    );
    if (orphanRoots.length > 0) {
      list.push({
        key: "__unassigned__",
        rootIndex: null,
        rootKey: null,
        rootRecord: null,
        docRootIndex: null,
        isUnassigned: true,
        orphanRoots,
      });
    }
    return list;
  }, [templateModel, dossierModel, dossiersBySection, initialDossiersBySection]);

  const unassignedDossierIndices = useMemo(() => {
    const bucket = groups.find((group) => group.isUnassigned);
    const out = new Set();
    (bucket?.orphanRoots || []).forEach((rootIndex) => {
      out.add(rootIndex);
      dossierSubtree(rootIndex).forEach((index) => out.add(index));
    });
    return out;
  }, [groups, dossierModel]);

  // Keys/indices that act as group banners in section-wise view → locked in All view
  const lockedRowKeys = useMemo(() => {
    const templateKeys = new Set();
    const docIndices = new Set();
    groups.forEach((group) => {
      if (group.isAll || group.isUnassigned) return;
      if (group.rootKey) templateKeys.add(group.rootKey);
      (group.docRootIndices || []).forEach((i) => docIndices.add(i));
    });
    return { templateKeys, docIndices };
  }, [groups]);

  const activeGroupIndex = Math.min(groupIndex, Math.max(groups.length - 1, 0));
  const activeGroup = groups[activeGroupIndex] || null;

  const groupLabel = (group) => {
    if (!group) return "";
    if (group.isAll) return "All sections";
    if (group.isUnassigned) return "Unassigned document headings";
    if (!group.rootRecord) return "";
    const { order, name } = group.rootRecord;
    return order ? `${order}. ${name}` : name;
  };

  const groupCountLabel = (group) => {
    if (!group) return "";
    if (group.isAll) {
      return `${mappedDossierCount}/${allViewTotalCount}`;
    }
    const counts = groupCounts[group.key] || { mapped: 0, total: 0 };
    return group.isUnassigned ? `${counts.total}` : `${counts.mapped}/${counts.total}`;
  };

  // ── Rows for the active group ───────────────────────────────────────────────
  const rows = useMemo(() => {
    if (!activeGroup) return [];
    const out = [];

    // ── "All" view: every template section + every dossier heading ──────
    if (activeGroup.isAll) {
      const walked = (templateModel.childrenByIndex["root"] || []).flatMap(
        (rootIndex) => [rootIndex, ...templateSubtree(rootIndex)]
      );
      const reached = new Set(walked);
      const allTemplateIndices = [
        ...walked,
        ...templateModel.records
          .map((_, index) => index)
          .filter((index) => !reached.has(index)),
      ];
      const consumed = new Set();
      allTemplateIndices.forEach((index) => {
        const record = templateModel.records[index];
        const linked = dossiersBySection[record.key] || [];
        const docIndex = linked.length > 0 ? linked[0] : null;
        if (docIndex !== null) consumed.add(docIndex);
        out.push({
          id: `L:${record.key}`,
          leftRecord: record,
          docIndex,
          leftIndent: record.depth - 1,
          rightIndent:
            docIndex !== null ? dossierModel.depthByIndex[docIndex] - 1 : 0,
          locked: lockedRowKeys.templateKeys.has(record.key) || lockedRowKeys.docIndices.has(docIndex),
        });
      });
      const allDossierIndices = Object.keys(dossierModel.byIndex).map(Number);
      allDossierIndices
        .filter((index) => !consumed.has(index) && !unassignedDossierIndices.has(index))
        .sort((a, b) => a - b)
        .forEach((dossierIndex) => {
          out.push({
            id: `R:${dossierIndex}`,
            leftRecord: null,
            docIndex: dossierIndex,
            leftIndent: 0,
            rightIndent: Math.max(dossierModel.depthByIndex[dossierIndex] - 1, 0),
            locked: lockedRowKeys.docIndices.has(dossierIndex),
          });
        });
      return out;
    }

    if (activeGroup.isUnassigned) {
      const members = [];
      activeGroup.orphanRoots.forEach((rootIndex) => {
        members.push(rootIndex, ...dossierSubtree(rootIndex));
      });
      members.sort((a, b) => a - b).forEach((dossierIndex) => {
        out.push({
          id: `R:${dossierIndex}`,
          leftRecord: null,
          docIndex: dossierIndex,
          leftIndent: 0,
          rightIndent: dossierModel.depthByIndex[dossierIndex] - 1,
        });
      });
      return out;
    }

    const rootRecord = activeGroup.rootRecord;
    const leftIndices = templateSubtree(activeGroup.rootIndex);

    const rightIndices = new Set();
    if (activeGroup.docRootIndex !== null) {
      if (!linksByDossier[activeGroup.docRootIndex]) {
        rightIndices.add(activeGroup.docRootIndex);
      }
      dossierSubtree(activeGroup.docRootIndex).forEach((i) => rightIndices.add(i));
    }
    leftIndices.forEach((index) => {
      (dossiersBySection[templateModel.records[index].key] || []).forEach((i) =>
        rightIndices.add(i)
      );
    });

    const docRootDepth =
      activeGroup.docRootIndex !== null
        ? dossierModel.depthByIndex[activeGroup.docRootIndex]
        : 0;

    const consumed = new Set();
    out.push({
      id: `L:${rootRecord.key}`,
      leftRecord: rootRecord,
      docIndex: activeGroup.docRootIndex,
      leftIndent: 0,
      rightIndent: 0,
      locked: true,
      isTopLevelListRow: true,
    });
    if (activeGroup.docRootIndex !== null) {
      consumed.add(activeGroup.docRootIndex);
    }

    leftIndices.forEach((index) => {
      const record = templateModel.records[index];
      const linked = dossiersBySection[record.key] || [];
      const docIndex = linked.length > 0 ? linked[0] : null;
      if (docIndex !== null) consumed.add(docIndex);
      out.push({
        id: `L:${record.key}`,
        leftRecord: record,
        docIndex,
        leftIndent: record.depth - rootRecord.depth - 1,
        rightIndent:
          docIndex !== null ? dossierModel.depthByIndex[docIndex] - docRootDepth - 1 : 0,
      });
    });

    Array.from(rightIndices)
      .filter((index) => !consumed.has(index))
      .sort((a, b) => a - b)
      .forEach((dossierIndex) => {
        out.push({
          id: `R:${dossierIndex}`,
          leftRecord: null,
          docIndex: dossierIndex,
          leftIndent: 0,
          rightIndent: Math.max(
            dossierModel.depthByIndex[dossierIndex] - docRootDepth - 1,
            0
          ),
        });
      });

    return out;
  }, [activeGroup, templateModel, dossierModel, dossiersBySection, linksByDossier, lockedRowKeys, unassignedDossierIndices]);

  const visibleRows = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    return rows.filter((row) => {
      if (!query) {
        if (row.leftRecord) {
          let parentIndex = row.leftRecord.parentIndex;
          while (parentIndex !== null) {
            const parent = templateModel.records[parentIndex];
            if (collapsedSectionKeys.has(parent.key)) return false;
            parentIndex = parent.parentIndex;
          }
        } else if (row.docIndex !== null) {
          let dossierIndex = row.docIndex;
          while (dossierIndex !== null && dossierIndex !== undefined) {
            const mappedSectionKey = linksByDossier[dossierIndex];
            if (mappedSectionKey && collapsedSectionKeys.has(mappedSectionKey)) return false;
            dossierIndex = dossierModel.byIndex[dossierIndex]?.parent_index;
          }
        }
      }

      if (
        row.leftRecord &&
        (row.leftRecord.key === editingKey || row.leftRecord.key === expandedAgentsKey)
      ) return true;
      const isMapped = !!(row.leftRecord && row.docIndex !== null);
      if (filterMode === "mapped" && !isMapped) return false;
      if (filterMode === "unmapped" && isMapped) return false;
      if (!query) return true;
      const docNode = row.docIndex !== null ? dossierModel.byIndex[row.docIndex] : null;
      const haystack = [
        row.leftRecord ? `${row.leftRecord.order} ${row.leftRecord.name}` : "",
        docNode ? `${docNode.section_ordering_id} ${docNode.heading}` : "",
      ].join(" ").toLowerCase();
      return haystack.includes(query);
    });
  }, [rows, filterMode, searchInput, dossierModel, editingKey, expandedAgentsKey, templateModel, collapsedSectionKeys, linksByDossier,]);

  const templateRows = useMemo(
    () => visibleRows.filter((row) => row.leftRecord),
    [visibleRows]
  );
  const docOnlyRows = useMemo(
    () => visibleRows.filter((row) => !row.leftRecord),
    [visibleRows]
  );

  const isReorderEnabled =
    !readOnly &&
    !!activeGroup && !activeGroup.isUnassigned && !activeGroup.isAll &&
    filterMode === "all" && searchInput.trim() === "";

  const dragContext = useMemo(() => {
    if (!dragCollapsedKey) return null;
    const record = templateModel.recordByKey[dragCollapsedKey];
    if (!record) return null;

    const bucket = record.parentIndex === null ? "root" : record.parentIndex;
    const folded = new Set();
    const band = new Set();
    (templateModel.childrenByIndex[bucket] || []).forEach((siblingIndex) => {
      band.add(templateModel.records[siblingIndex].key);
      templateSubtree(siblingIndex).forEach((index) =>
        folded.add(templateModel.records[index].key)
      );
    });

    return { folded, band };
  }, [dragCollapsedKey, templateModel]);

  const renderedTemplateRows = useMemo(() => {
    if (!dragContext) return templateRows;

    const rows = templateRows.filter(
      (row) => !dragContext.folded.has(row.leftRecord.key)
    );
    const firstBandIndex = rows.findIndex((row) =>
      dragContext.band.has(row.leftRecord.key)
    );
    if (firstBandIndex === -1) return rows;

    let lastBandIndex = firstBandIndex;
    for (let index = firstBandIndex + 1; index < rows.length; index += 1) {
      if (dragContext.band.has(rows[index].leftRecord.key)) {
        lastBandIndex = index;
      }
    }

    return rows.slice(
      Math.max(0, firstBandIndex - 4),
      Math.min(rows.length, lastBandIndex + 5)
    );
  }, [dragContext, templateRows]);

  const bandClassFor = (row) => {
    if (!dragContext) return "";
    return dragContext.band.has(row.leftRecord?.key)
      ? " genbi-map-in-band"
      : " genbi-map-out-of-band";
  };

  const isSelectionSource = (row) =>
    !!selection &&
    (selection.side === "template"
      ? row.leftRecord?.key === selection.key
      : row.docIndex === selection.key);

  const isMappingTarget = (row) => {
    if (!selection || row.locked) return false;
    if (selection.side === "doc") {
      return !!row.leftRecord && row.docIndex === null;
    }
    // Mirrors the doc cell's own `isTarget`: a heading already claimed by a
    // section that still exists cannot take another mapping.
    const claimedBy = templateModel.recordByKey[linksByDossier[row.docIndex]];
    return row.docIndex !== null && !row.leftRecord && !claimedBy;
  };

  const selectionDimClass = (row) => {
    if (!selection || dragContext) return "";
    return isSelectionSource(row) || isMappingTarget(row)
      ? ""
      : " genbi-map-dimmed";
  };

  // Resolve the owning group to preserve the existing locked-row styling.
  const groupIndexForRow = (row) => {
    if (row.leftRecord) {
      const index = groups.findIndex((group) => group.key === row.leftRecord.key);
      if (index >= 0) return index;
    }
    if (row.docIndex === null || row.docIndex === undefined) return -1;
    return groups.findIndex((group) =>
      (group.docRootIndices || []).includes(row.docIndex)
    );
  };

  const renderedRowsRef = useRef([]);
  renderedRowsRef.current = renderedTemplateRows;

  const groupCounts = useMemo(() => {
    const counts = {};
    groups.forEach((group) => {
      if (group.isAll) return;

      const groupDossierIndices = new Set();
      if (group.isUnassigned) {
        (group.orphanRoots || []).forEach((rootIndex) => {
          groupDossierIndices.add(rootIndex);
          dossierSubtree(rootIndex).forEach((i) => groupDossierIndices.add(i));
        });
      } else {
        const subtreeKeys = [
          group.rootKey,
          ...templateSubtree(group.rootIndex).map((i) => templateModel.records[i].key),
        ];
        subtreeKeys.forEach((key) => {
          (dossiersBySection[key] || []).forEach((i) => groupDossierIndices.add(i));
        });
        if (group.docRootIndex !== null) {
          groupDossierIndices.add(group.docRootIndex);
          dossierSubtree(group.docRootIndex).forEach((i) => groupDossierIndices.add(i));
        }
      }

      const total = groupDossierIndices.size;
      const mapped = [...groupDossierIndices].filter((i) => linksByDossier[i] !== undefined).length;
      counts[group.key] = { mapped, total, dropped: Math.max(total - mapped, 0) };
    });
    return counts;
  }, [groups, templateModel, dossierModel, dossiersBySection, linksByDossier]);

  const mappedDossierCount = Object.keys(linksByDossier).length;
  const totalDossierCount = dossierOrder.length;

  const allViewTotalCount = Math.max(
    totalDossierCount - unassignedDossierIndices.size, 0
  );

  const activeDroppedCount = useMemo(() => {
    if (!activeGroup) return 0;
    if (activeGroup.isAll) return Math.max(allViewTotalCount - mappedDossierCount, 0);
    return groupCounts[activeGroup.key]?.dropped ?? 0;
  }, [activeGroup, groupCounts, mappedDossierCount, allViewTotalCount]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleToggleSelect = (side, key) => {
    setSelection((prev) =>
      prev && prev.side === side && prev.key === key ? null : { side, key }
    );
  };

  const saveUndoAction = (message) => {
    setUndoAction({
      message,
      sections,
      linksByDossier,
      matchTypeByDossier,
    });
  };

  const handleUndo = () => {
    if (!undoAction) return;
    setSections(undoAction.sections);
    setLinksByDossier(undoAction.linksByDossier);
    setMatchTypeByDossier(undoAction.matchTypeByDossier);
    setSelection(null);
    setUndoAction(null);
  };

  const handleMapPair = (sectionKey, dossierIndex) => {
    saveUndoAction("Mapping updated");
    setLinksByDossier((prev) => ({ ...prev, [dossierIndex]: sectionKey }));
    setMatchTypeByDossier((prev) => ({ ...prev, [dossierIndex]: "manual" }));
    setSelection(null);

    if (!hasOtherHeadings(sectionKey, dossierIndex)) {
      retitlePlaceholder(sectionKey, dossierModel.byIndex[dossierIndex]?.heading);
    }
  };

  const hasOtherHeadings = (sectionKey, dossierIndex) =>
    (dossiersBySection[sectionKey] || []).some((index) => index !== dossierIndex);

  // A placeholder's title follows its mapping unless the user renamed it by hand.
  // applyRename refuses a title a subsection or a section number already uses.
  const retitlePlaceholder = (sectionKey, rawTitle) => {
    const record = templateModel.recordByKey[sectionKey];
    const title = String(rawTitle ?? "").trim();
    if (readOnly || !record?.isPlaceholder || !title || customTitleKeys.has(sectionKey)) return;
    const nextSections = applyRename(
      sections, templateModel, sectionKey, title, initialState.parentMode
    );
    if (!nextSections) return;
    if (editingKey === sectionKey) cancelRename();
    setSections(nextSections);
  };

  const releaseRemovedSections = (removedKeys, alsoUnlink = null) => {
    setLinksByDossier((prev) => {
      const next = { ...prev };
      if (alsoUnlink !== null) delete next[alsoUnlink];
      Object.keys(next).forEach((index) => {
        if (removedKeys.has(next[index])) delete next[index];
      });
      return next;
    });
    if (removedKeys.size > 0) {
      if (editingKey && removedKeys.has(editingKey)) cancelRename();
      if (expandedAgentsKey && removedKeys.has(expandedAgentsKey)) {
        setExpandedAgentsKey(null);
      }
    }
    setSelection(null);
  };

  const handleUnlink = (sectionKey, dossierIndex) => {
    saveUndoAction("Mapping removed");
    const record = templateModel.recordByKey[sectionKey];
    const isAiAdded = !!record?.section?.__aiAdded;

    const removedKeys = new Set(
      isAiAdded ? deletionKeys(templateModel, sectionKey) : []
    );
    if (removedKeys.size > 0) {
      setSections((prev) => prev.filter((section) => !removedKeys.has(section.__key)));
    } else if (!hasOtherHeadings(sectionKey, dossierIndex)) {
      retitlePlaceholder(sectionKey, placeholderNames[sectionKey]);
    }
    releaseRemovedSections(removedKeys, dossierIndex);
  };

  const subtreeNames = useMemo(() => {
    if (!editingKey) return null;
    const record = templateModel.recordByKey[editingKey];
    if (!record) return null;
    return new Set(
      subtreeIndices(templateModel, record.index)
        .map((index) => normName(templateModel.records[index].name))
        .filter(Boolean)
    );
  }, [editingKey, templateModel]);

  const renameIssue = useMemo(() => {
    if (!editingKey || !subtreeNames) return null;
    const record = templateModel.recordByKey[editingKey];
    if (!record) return null;
    const name = draftName.trim();
    if (!name) return "Section name cannot be empty";
    const norm = normName(name);
    if (
      initialState.parentMode === "name" &&
      norm && norm !== normName(record.name) && subtreeNames.has(norm)
    ) {
      return "A subsection already uses this name — pick a different one";
    }
    if (renameLooksLikeOrderingId(templateModel, record, name)) {
      return "Section name cannot be a section number like 5.3 — pick a different one";
    }
    return null;
  }, [editingKey, draftName, subtreeNames, templateModel, initialState.parentMode]);

  const startRename = (record) => {
    setEditingKey(record.key);
    setDraftName(record.name);
    pendingRenameRef.current = { key: record.key, name: record.name };
    setSelection(null);
  };

  const closeRenameEditor = () => {
    setEditingKey(null);
    setDraftName("");
  };

  const cancelRename = () => {
    pendingRenameRef.current = null;
    closeRenameEditor();
  };

  const handleRenameChange = (value) => {
    setDraftName(value);
    if (editingKey) pendingRenameRef.current = { key: editingKey, name: value };
  };

  const commitRename = () => {
    if (!editingKey) return;
    if (renameIssue) return;
    const nextSections = applyRename(
      sections, templateModel, editingKey, draftName, initialState.parentMode
    );
    if (nextSections) {
      setSections(nextSections);
      if (templateModel.recordByKey[editingKey]?.isPlaceholder) {
        setCustomTitleKeys((prev) => new Set(prev).add(editingKey));
      }
    }
    closeRenameEditor();
  };

  const handleRenameKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      commitRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelRename();
    }
  };

  const handleRenameBlur = () => (renameIssue ? cancelRename() : commitRename());

  const canDeleteSection = (record) => !!record && record.depth > 1;

  const requestDeleteSection = (record) => {
    if (!canDeleteSection(record)) return;
    const removedKeys = deletionKeys(templateModel, record.key);
    const mapped = removedKeys.reduce(
      (count, key) => count + (dossiersBySection[key] || []).length, 0
    );
    setPendingDelete({
      key: record.key,
      label: record.order ? `${record.order} ${record.name}` : record.name,
      subsectionCount: removedKeys.length - 1,
      mappedCount: mapped,
    });
  };

  const confirmDeleteSection = () => {
    if (!pendingDelete) return;
    const record = templateModel.recordByKey[pendingDelete.key];
    if (!record) {                       // vanished from under us — nothing to do
      setPendingDelete(null);
      return;
    }
    const removedKeys = new Set(deletionKeys(templateModel, pendingDelete.key));
    const nextSections = applySectionDelete(
      sections, templateModel, initialState.parentMode, pendingDelete.key
    );
    if (nextSections) setSections(nextSections);
    releaseRemovedSections(removedKeys);
    setPendingDelete(null);
  };

  // ── Agents of a section ─────────────────────────────────────────────────────
  const withNewAgentKey = (agent) => ({
    ...agent,
    __agentKey: `ag_new_${(agentSeqRef.current += 1)}`,
    __added: true,
  });

  const handleToggleAgents = (key) => {
    setLastAddedAgentKey(null);
    setExpandedAgentsKey((prev) => (prev === key ? null : key));
  };

  const handleAddAgent = (record) => {
    const agents = Array.isArray(record.section.agents) ? record.section.agents : [];
    if (agents.length >= MAX_AGENTS_PER_SECTION) return;
    const agent = withNewAgentKey({
      agent_name: nextAgentName(agents),
      agent_context: "",
      agent_ordering_id: agents.length + 1,
    });
    setSections((prev) =>
      applyAgentChange(prev, record.key, (list) =>
        list.length >= MAX_AGENTS_PER_SECTION ? null : [...list, agent]
      )
    );
    setLastAddedAgentKey(agent.__agentKey);
  };

  const handleDeleteAgent = (sectionKey, agentKey) => {
    setSections((prev) =>
      applyAgentChange(prev, sectionKey, (list, section) => {
        const target = list.find((agent) => agent.__agentKey === agentKey);
        if (!target || !isAgentEditable(section, target)) return null;
        return list.filter((agent) => agent.__agentKey !== agentKey);
      })
    );
  };

  const handleRenameAgent = (sectionKey, agentKey, rawName) => {
    const name = String(rawName ?? "").trim();
    if (!name) return;
    setSections((prev) =>
      applyAgentChange(prev, sectionKey, (list, section) => {
        const target = list.find((agent) => agent.__agentKey === agentKey);
        if (!target || !isAgentEditable(section, target)) return null;
        if (target.agent_name === name) return null;
        return list.map((agent) =>
          agent.__agentKey === agentKey ? { ...agent, agent_name: name } : agent
        );
      })
    );
  };

  const validateAgentName = () => (value) =>
    String(value ?? "").trim() ? null : "Agent name cannot be empty";

  // ── Reorder + level change ──────────────────────────────────────────────────
  const siblingKeysOf = (record) => {
    const bucket = record.parentIndex === null ? "root" : record.parentIndex;
    return (templateModel.childrenByIndex[bucket] || []).map(
      (index) => templateModel.records[index].key
    );
  };

  const subtreeMaxDepth = (record) =>
    templateSubtree(record.index).reduce(
      (deepest, index) => Math.max(deepest, templateModel.records[index].depth),
      record.depth
    );

  const applyMove = (move) => {
    const nextSections = applyStructureMove(
      sections, templateModel, initialState.parentMode, move
    );
    if (!nextSections) {
      console.error("[HeadingMapping] Discarded an unsafe structure change", move);
      return;
    }
    saveUndoAction("Heading order updated");
    setSections(nextSections);
    setSelection(null);
  };

  const indentTargetKey = (record) => {
    if (!record) return null;
    const siblings = siblingKeysOf(record);
    const position = siblings.indexOf(record.key);
    if (position <= 0) return null;
    const previous = templateModel.recordByKey[siblings[position - 1]];
    if (!previous) return null;
    if (subtreeMaxDepth(record) + 1 > MAX_INDENT_DEPTH) return null;
    return previous.key;
  };

  const canOutdent = (record) => {
    if (!record) return false;
    if (!activeGroup || activeGroup.isUnassigned || activeGroup.isAll) return false;
    return record.depth - activeGroup.rootRecord.depth >= 2;
  };

  const handleIndent = (record) => {
    const targetKey = indentTargetKey(record);
    if (!targetKey) return;
    const target = templateModel.recordByKey[targetKey];
    applyMove({
      key: record.key,
      targetParentKey: targetKey,
      targetIndex: (templateModel.childrenByIndex[target.index] || []).length,
    });
  };

  const handleOutdent = (record) => {
    if (!canOutdent(record)) return;
    const parent = templateModel.records[record.parentIndex];
    const grandBucket =
      parent.parentIndex === null ? "root" : parent.parentIndex;
    const grandParentKey =
      parent.parentIndex === null ? "root" : templateModel.records[parent.parentIndex].key;
    const grandSiblings = (templateModel.childrenByIndex[grandBucket] || []).map(
      (index) => templateModel.records[index].key
    );
    applyMove({
      key: record.key,
      targetParentKey: grandParentKey,
      targetIndex: grandSiblings.indexOf(parent.key) + 1,
    });
  };

  const handleBeforeCapture = ({ draggableId }) => {
    const container = scrollRef.current;

    dragRestoreRef.current = container
      ? { key: draggableId, scrollTop: container.scrollTop }
      : null;
    const topBefore = rowNodeFor(container, draggableId)?.getBoundingClientRect().top;

    flushSync(() => {
      setExpandedAgentsKey(null);
      setDragCollapsedKey(draggableId);
    });

    const topAfter = rowNodeFor(container, draggableId)?.getBoundingClientRect().top;
    if (container && topBefore !== undefined && topAfter !== undefined) {
      container.scrollTop += topAfter - topBefore;
    }

    const viewport = container?.getBoundingClientRect();
    dragBoundsRef.current = viewport
      ? { top: viewport.top, bottom: viewport.bottom }
      : null;
  };

  const handleDragEnd = ({ draggableId, source, destination }) => {
    setDragCollapsedKey(null);
    if (!destination || destination.index === source.index) return;

    const record = templateModel.recordByKey[draggableId];
    if (!record) return;
    const parentKey =
      record.parentIndex === null ? "root" : templateModel.records[record.parentIndex].key;

    const withoutDragged = renderedRowsRef.current.filter(
      (row) => row.leftRecord.key !== draggableId
    );
    const siblings = new Set(siblingKeysOf(record));
    siblings.delete(draggableId);

    let targetIndex = 0;
    const stop = Math.min(destination.index, withoutDragged.length);
    for (let i = 0; i < stop; i++) {
      if (siblings.has(withoutDragged[i].leftRecord.key)) targetIndex += 1;
    }

    applyMove({ key: draggableId, targetParentKey: parentKey, targetIndex });
  };

  useLayoutEffect(() => {
    if (dragCollapsedKey !== null) return;
    const pending = dragRestoreRef.current;
    if (!pending) return;
    dragRestoreRef.current = null;

    const container = scrollRef.current;
    if (!container) return;
    container.scrollTop = pending.scrollTop;

    const node = rowNodeFor(container, pending.key);
    if (!node) return;
    const row = node.getBoundingClientRect();
    const box = container.getBoundingClientRect();
    if (row.top < box.top) {
      container.scrollTop -= box.top - row.top;
    } else if (row.bottom > box.bottom) {
      container.scrollTop += row.bottom - box.bottom;
    }
  }, [dragCollapsedKey]);

  // Anchor a new section under the nearest document ancestor that is mapped.
  const resolveAnchorParentKey = (dossierIndex) => {
    let parent = dossierModel.byIndex[dossierIndex]?.parent_index;
    const seen = new Set();
    while (parent !== null && parent !== undefined && !seen.has(parent)) {
      seen.add(parent);
      const key = linksByDossier[parent];
      if (key && templateModel.recordByKey[key]) return key;
      parent = dossierModel.byIndex[parent]?.parent_index;
    }
    return activeGroup && !activeGroup.isUnassigned ? activeGroup.rootKey : null;
  };

  const nextOrderingId = (parentKey, siblingIndex, usedOrderingIds) => {
    const parentRecord = parentKey ? templateModel.recordByKey[parentKey] : null;
    const parentOrder = parentRecord ? parentRecord.order : "";
    const prefix = parentOrder ? `${parentOrder}.` : "";
    const expectedDepth = parentOrder ? orderingDepth(parentOrder) + 1 : 1;
    let maxNumber = 0;
    usedOrderingIds.forEach((orderingId) => {
      if (orderingDepth(orderingId) !== expectedDepth) return;
      if (!orderingId.startsWith(prefix)) return;
      const suffix = orderingId.slice(prefix.length);
      if (/^\d+$/.test(suffix)) maxNumber = Math.max(maxNumber, Number(suffix));
    });

    const desired = `${prefix}${siblingIndex}`;
    if (siblingIndex <= maxNumber + 1 && !usedOrderingIds.has(desired)) return desired;

    let candidate = `${prefix}${maxNumber + 1}`;
    let next = maxNumber + 1;
    while (usedOrderingIds.has(candidate)) {
      next += 1;
      candidate = `${prefix}${next}`;
    }
    return candidate;
  };

  const resolveInsertPosition = (dossierIndex, parentKey) => {
    const node = dossierModel.byIndex[dossierIndex];
    const parentBucket =
      node?.parent_index === null || node?.parent_index === undefined
        ? "root"
        : node.parent_index;
    const siblings = dossierModel.childrenByIndex[parentBucket] || [];
    const position = siblings.indexOf(dossierIndex);

    const sectionIndexOfKey = (key) =>
      sections.findIndex((section) => section.__key === key);

    const subtreeEnd = (key) => {
      const record = templateModel.recordByKey[key];
      if (!record) return sectionIndexOfKey(key);
      return templateSubtree(record.index).reduce(
        (last, index) =>
          Math.max(last, sectionIndexOfKey(templateModel.records[index].key)),
        sectionIndexOfKey(key)
      );
    };

    const isChildOfParent = (key) =>
      templateModel.recordByKey[key]?.parentIndex ===
      (parentKey ? templateModel.recordByKey[parentKey]?.index : null);

    for (let i = position - 1; i >= 0; i--) {
      const key = linksByDossier[siblings[i]];
      if (key && isChildOfParent(key)) return subtreeEnd(key) + 1;
    }
    for (let i = position + 1; i < siblings.length; i++) {
      const key = linksByDossier[siblings[i]];
      if (key && isChildOfParent(key)) return sectionIndexOfKey(key);
    }
    if (parentKey) return subtreeEnd(parentKey) + 1;
    return sections.length;
  };

  const handleAddToTemplate = (dossierIndex) => {
    const node = dossierModel.byIndex[dossierIndex];
    if (!node) return;

    const parentKey = resolveAnchorParentKey(dossierIndex);
    const parentRecord = parentKey ? templateModel.recordByKey[parentKey] : null;
    const usedOrderingIds = new Set(
      sections
        .map((section) => String(section.section_ordering_id ?? "").trim())
        .filter(Boolean)
    );
    const orderingId = nextOrderingId(
      parentKey,
      dossierModel.siblingIndexByIndex[dossierIndex] || 1,
      usedOrderingIds
    );

    let parentReference = null;
    if (parentRecord) {
      parentReference =
        initialState.parentMode === "name"
          ? parentRecord.section.section_name
          : parentRecord.section.section_ordering_id;
    }

    const newKey = `sec_new_${dossierIndex}_${Date.now()}`;
    const newSection = {
      section_name: node.heading,
      parent_section_id: parentReference,
      section_ordering_id: orderingId,
      agents: [withNewAgentKey(defaultAgentFor(node.heading))],
      AI_Modified: true,
      __key: newKey,
      __aiAdded: true,
    };

    const insertAt = resolveInsertPosition(dossierIndex, parentKey);
    setSections((prev) => {
      const next = [...prev];
      next.splice(Math.max(0, Math.min(insertAt, next.length)), 0, newSection);
      return next;
    });
    setLinksByDossier((prev) => ({ ...prev, [dossierIndex]: newKey }));
    setMatchTypeByDossier((prev) => ({ ...prev, [dossierIndex]: "manual" }));
    setSelection(null);
  };

  const handleConfirm = () => {
    const pending = pendingRenameRef.current;
    const renamed = pending
      ? applyRename(
          sections, templateModel, pending.key, pending.name, initialState.parentMode
        )
      : null;
    const finalSections = renamed || sections;
    const unpinReferences = unpinReferAgents(finalSections);

    const amendedSections = finalSections.map((section) => {
      const { __key, __aiAdded, ...rest } = section;
      const linked = dossiersBySection[__key] || [];
      const matches = linked.map((dossierIndex) => ({
        dossier_index: dossierIndex,
        dossier_heading: dossierModel.byIndex[dossierIndex]?.heading || "",
        match_type: matchTypeByDossier[dossierIndex] || "manual",
      }));
      const payload = {
        ...rest,
        matched_section:
          matches.length === 0 ? null : matches.length === 1 ? matches[0] : matches,
      };
      if (Array.isArray(rest.agents)) {
        payload.agents = rest.agents.map((agent) => {
          const { __agentKey, __added, ...clean } = unpinReferences(agent);
          return clean;
        });
      }
      return payload;
    });

    onConfirm({
      ...(mappingData?.amended_template || {}),
      sections: amendedSections,
    });
  };

  // ── Cell renderers ──────────────────────────────────────────────────────────
  const rowIndentStyle = (row) => {
    const left = Math.min(Math.max(Number(row.leftIndent) || 0, 0), MAX_INDENT_DEPTH);
    const right = Math.min(Math.max(Number(row.rightIndent) || 0, 0), MAX_INDENT_DEPTH);
    return { "--genbi-map-depth": left, "--genbi-map-depth-end": right };
  };

  const renderAgentsPanel = (record) => {
    const section = record.section;
    const agents = Array.isArray(section.agents) ? section.agents : [];
    const atCap = agents.length >= MAX_AGENTS_PER_SECTION;

    return (
      <div className="genbi-map-agents genbi-map-indent">
        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
          <span className="ev-xsmall-p-blue genbi-map-text">
            <i className="bi bi-robot me-1"></i>
            Agents of “{record.name}”
          </span>
          {!readOnly && (
            <span
              className="avatar-tooltip flex-shrink-0"
              data-tooltip={
                atCap
                  ? `Max ${MAX_AGENTS_PER_SECTION} agents allowed`
                  : "Add agent"
              }
            >
              <button
                type="button"
                className="ev-btn-blue-outline-round ev-btn-xsmall py-0 px-2"
                disabled={atCap}
                onClick={() => handleAddAgent(record)}
              >
                <i className="bi bi-plus-lg me-1"></i>Add agent
              </button>
            </span>
          )}
        </div>

        {agents.length === 0 && (
          <p className="ev-xsmall-p-dark genbi-map-muted mb-0">
            This section has no agents, add one, or its content will not be
            imported.
          </p>
        )}

        {agents.map((agent, index) => {
          const editable = !readOnly && isAgentEditable(section, agent);
          return (
            <div
              key={agent.__agentKey || index}
              className="genbi-map-agent-row d-flex align-items-center gap-1"
            >
              <span className="genbi-map-oid flex-shrink-0">{index + 1}</span>
              {editable ? (
                <AgentNameField
                  value={String(agent.agent_name ?? "")}
                  autoFocus={agent.__agentKey === lastAddedAgentKey}
                  validate={validateAgentName()}
                  onCommit={(name) =>
                    handleRenameAgent(record.key, agent.__agentKey, name)
                  }
                />
              ) : (
                <>
                  <span
                    className="genbi-map-text ev-xsmall-p-dark flex-grow-1"
                    title={agent.agent_name}
                  >
                    {agent.agent_name || (
                      <span className="genbi-map-muted"></span>
                    )}
                  </span>
                  {!readOnly && (
                    <i
                      className="bi bi-lock flex-shrink-0 ev-xsmall-p-blue avatar-tooltip"
                      data-tooltip="This agent comes from the preset — it cannot be renamed or removed"
                    ></i>
                  )}
                </>
              )}
              {editable && (
                <span
                  className="avatar-tooltip flex-shrink-0"
                  data-tooltip="Delete agent"
                >
                  <button
                    type="button"
                    className="ev-btn-red-outline-round ev-btn-xsmall py-0 px-1"
                    onClick={() => handleDeleteAgent(record.key, agent.__agentKey)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTemplateCell = (row, dragProvided) => {
    if (!row.leftRecord) {
      return (
        <div className="genbi-map-cell genbi-map-indent is-empty">
          <span className="ev-xsmall-p-dark genbi-map-muted">Not in preset</span>
        </div>
      );
    }

    const record = row.leftRecord;
    const isMapped = row.docIndex !== null;
    const isSelected = selection?.side === "template" && selection.key === record.key;
    const isTarget = !isMapped && !row.locked && selection?.side === "doc";
    const isClickable = !readOnly && !isMapped && !row.locked;
    const isSummary = isSummarySection(record.section);
    const hasSiblings = siblingKeysOf(record).length > 1;
    const canDrag = isReorderEnabled && hasSiblings;
    const isEditing = editingKey === record.key;
    const canRename = !readOnly;
    const outdentAllowed = canOutdent(record);
    const indentAllowed = !!indentTargetKey(record);
    const canJump = !!row.locked && groupIndexForRow(row) >= 0;
    const hasChildren = (templateModel.childrenByIndex[record.index] || []).length > 0;
    const isSectionCollapsed = collapsedSectionKeys.has(record.key);

    const handleClick = () => {
      if (!isClickable) return;
      if (isTarget) handleMapPair(record.key, selection.key);
      else handleToggleSelect("template", record.key);
    };

    const controls = (
      <span
        className="genbi-map-controls d-inline-flex align-items-center gap-1 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <span
          {...(dragProvided ? dragProvided.dragHandleProps : {})}
          className={`genbi-map-drag-handle avatar-tooltip${canDrag ? "" : " is-disabled"}`}
          data-tooltip={
            row.isTopLevelListRow
              ? "Top-level sections cannot be reordered"
              : !isReorderEnabled
              ? "Clear the filter and search to reorder"
              : !hasSiblings
                ? "Nothing to reorder — this section has no siblings"
                : "Drag to reorder within the same level"
          }
        >
          <i className="bi bi-grip-vertical"></i>
        </span>
        <button
          type="button"
          className="ev-btn-blue-outline-round ev-btn-xsmall py-0 px-1 avatar-tooltip"
          data-tooltip="Move out one level"
          disabled={!outdentAllowed}
          onClick={() => handleOutdent(record)}
        >
          <i className="bi bi-chevron-left"></i>
        </button>
        <button
          type="button"
          className="ev-btn-blue-outline-round ev-btn-xsmall py-0 px-1 avatar-tooltip"
          data-tooltip="Nest under the section above"
          disabled={!indentAllowed}
          onClick={() => handleIndent(record)}
        >
          <i className="bi bi-chevron-right"></i>
        </button>
      </span>
    );

    return (
      <div className="d-flex align-items-center gap-1 genbi-map-indent">
      <div
        className={`genbi-map-cell flex-grow-1${isClickable ? " is-clickable" : ""}`
          + `${canJump ? " is-jumpable" : ""}`
          + `${isSelected ? " is-selected" : ""}`
          + `${record.section.__aiAdded ? " is-added" : ""}`}
        style={{ minWidth: 0 }}
        onClick={handleClick}

      >
        <div className="d-flex align-items-center gap-1">
          {hasChildren && (
            <button
              type="button"
              className="ev-btn-xsmall py-0 px-1 flex-shrink-0 border-0 bg-transparent ev-xsmall-p-blue"
              aria-label={`${isSectionCollapsed ? "Expand" : "Collapse"} subsections of ${record.name || "untitled section"}`}
              aria-expanded={!isSectionCollapsed}
              title={`${isSectionCollapsed ? "Expand" : "Collapse"} subsections`}
              onClick={(event) => {
                event.stopPropagation();
                handleToggleSection(record.key);
              }}
            >
              <i className={`bi ${isSectionCollapsed ? "bi-chevron-right" : "bi-chevron-down"}`}></i>
            </button>
          )}
          {!readOnly && !(activeGroup && activeGroup.isAll) && controls}
          {/* Sits on the connector side of the cell, after the controls so their
              left edge stays aligned across every row. */}
          {isTarget && <span className="ev-btn-dark-blue-fill-round ev-btn-xsmall flex-shrink-0">Map here</span>}
          {record.order && <span className="genbi-map-oid">{record.order}</span>}
          {isEditing ? (
            <>
              <input
                type="text"
                autoFocus
                className={`genbi-map-rename-input ev-xsmall-p-dark flex-grow-1${
                  renameIssue ? " is-invalid" : ""
                }`}
                value={draftName}
                onChange={(e) => handleRenameChange(e.target.value)}
                onFocus={(e) => e.target.select()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleRenameKeyDown}
                onBlur={handleRenameBlur}
              />
              {renameIssue && (
                <i
                  className="bi bi-exclamation-circle text-danger flex-shrink-0 avatar-tooltip"
                  data-tooltip={renameIssue}
                ></i>
              )}
            </>
          ) : (
            <>
              <span className="genbi-map-text ev-xsmall-p-dark flex-grow-1" title={record.name}>
                {record.name || <span className="genbi-map-muted">Untitled section</span>}
              </span>
              {canRename && (
                <span
                  className="genbi-map-rename-btn avatar-tooltip flex-shrink-0"
                  data-tooltip="Rename this section"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="ev-btn-blue-outline-round ev-btn-xsmall py-0 px-1"
                    onClick={() => startRename(record)}
                  >
                    <i className="bi bi-pencil"></i>
                  </button>
                </span>
              )}
            </>
          )}
          {(() => {
            const agentCount = (record.section.agents || []).length;
            const isAgentsOpen = expandedAgentsKey === record.key;
            return (
              <span
                className={`genbi-map-agent-toggle d-inline-flex align-items-center gap-1 flex-shrink-0 avatar-tooltip ev-xsmall-p-blue${
                  agentCount === 0 ? " text-danger" : ""
                }`}
                // data-tooltip={`This section has ${agentCount} agent${agentCount <= 1 ? "" : "s"}. Click to edit them.`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleAgents(record.key);
                }}
              >
                <i className="bi bi-robot"></i>
                <span>{agentCount}</span>
                <i className={`bi ${isAgentsOpen ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
              </span>
            );
          })()}
          {record.isPlaceholder && (
            <i
              className="bi bi-braces flex-shrink-0 ev-xsmall-p-blue avatar-tooltip"
              data-tooltip="Placeholder section"
            ></i>
          )}
        </div>
      </div>
        {!readOnly && canDeleteSection(record) && (
          <button
            type="button"
            className="ev-btn-red-fill-round ev-radius-xsmall ev-icon-btn-padding-small ev-fs-10 ms-1 me-1 avatar-tooltip-bottom"
            onClick={() => requestDeleteSection(record)}
          >
            <i className="bi bi-trash"></i>
          </button>
        )}
      </div>
    );
  };

  const renderDocCell = (row) => {
    if (row.docIndex === null) {
      return (
        <div className="genbi-map-cell genbi-map-indent-end is-empty">
          <span className="ev-xsmall-p-dark d-block text-end genbi-map-muted">Not in Document but present in preset</span>
        </div>
      );
    }

    const node = dossierModel.byIndex[row.docIndex];
    if (!node) return null;

    const isMapped = !!row.leftRecord;
    const elsewhereKey = !isMapped ? linksByDossier[row.docIndex] : null;
    const elsewhereRecord = elsewhereKey ? templateModel.recordByKey[elsewhereKey] : null;
    const isSelected = selection?.side === "doc" && selection.key === row.docIndex;
    const isTarget = !isMapped && !elsewhereRecord && !row.locked && selection?.side === "template";
    const isClickable = !readOnly && !isMapped && !elsewhereRecord && !row.locked;
    const isDropped = !isMapped && !elsewhereRecord;
    const canAddAsSection =
      isClickable && resolveAnchorParentKey(row.docIndex) !== null;
    const matchType = matchTypeByDossier[row.docIndex];
    const badge = isMapped ? MATCH_BADGES[matchType] : null;

    const canJump = !!row.locked && groupIndexForRow(row) >= 0;

    const handleClick = () => {
      if (!isClickable) return;
      if (isTarget) handleMapPair(selection.key, row.docIndex);
      else handleToggleSelect("doc", row.docIndex);
    };

    return (
      <div className="d-flex align-items-center gap-1 genbi-map-indent-end">
        <div
          className={`genbi-map-cell flex-grow-1${isClickable ? " is-clickable" : ""}${canJump ? " is-jumpable" : ""}${isDropped ? " is-dropped" : ""}${isSelected ? " is-selected" : ""}`}
          style={{ minWidth: 0 }}
          onClick={handleClick}
          
        >
          <div className="d-flex align-items-center gap-1">
            {elsewhereRecord && (
              <span
                className="ev-message-card6 ev-btn-xsmall border-0 ev-xsmall-p-blue flex-shrink-0 avatar-tooltip"
                data-tooltip={`Mapped to "${elsewhereRecord.name}"`}
              >
                Mapped to {elsewhereRecord.order || elsewhereRecord.name}
              </span>
            )}
            {node.section_ordering_id && (
              <span className="genbi-map-oid is-doc">{node.section_ordering_id}</span>
            )}
            <span className="genbi-map-text ev-xsmall-p-dark flex-grow-1 text-end" title={node.heading}>{node.heading}</span>
            
            {badge && (
              <span className={`${badge.className} ev-btn-xsmall flex-shrink-0`}>{badge.label}</span>
            )}
            {/* Connector side of this cell, mirroring the template cell. */}
            {isTarget && <span className="ev-btn-dark-blue-fill-round ev-btn-xsmall flex-shrink-0">Map here</span>}
          </div>
        </div>
        {canAddAsSection && (
          <span className="avatar-tooltip flex-shrink-0" data-tooltip="Add to preset as a new section">
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-xsmall py-0 px-1"
              onClick={(e) => { e.stopPropagation(); handleAddToTemplate(row.docIndex); }}
            >
              <i className="bi bi-plus-lg"></i>
            </button>
          </span>
        )}
      </div>
    );
  };

  const renderConnector = (row) => {
    if (row.leftRecord && row.docIndex !== null) {
      const isLocked = readOnly || !!row.locked;
      return (
        <div className="genbi-map-connector">
          <span className="genbi-map-connector-line"></span>
          <span className="avatar-tooltip" data-tooltip={isLocked ? (readOnly ? "Mapped" : "Mapping for top level sections cannot be changed") : "Click to Unlink"}>
            <button
              type="button"
              className="genbi-map-connector-btn"
              disabled={isLocked}
              onClick={() => handleUnlink(row.leftRecord.key, row.docIndex)}
            >
              <i className={`bi bi-link-45deg${isLocked ? " ev-reg-p-muted" : " ev-reg-p-blue"}`}></i>
            </button>
          </span>
          <span className="genbi-map-connector-line"></span>
        </div>
      );
    }
    return (
      <div className="genbi-map-connector">
        <span className="genbi-map-connector-line is-dashed"></span>
      </div>
    );
  };

  if (!isOpen || !mappingData) return null;

  /*
  const activeDocRoot =
    activeGroup && activeGroup.docRootIndex !== null
      ? dossierModel.byIndex[activeGroup.docRootIndex]
      : null;
  */

  // ── Main render ─────────────────────────────────────────────────────────────
  return createPortal(
    <div
      className="ev-modal-overlay d-flex align-items-center justify-content-center p-3"
      style={{ zIndex: "var(--num-1000000000, 1000000000)" }}
    >
      <div
        className="ev-card-4 d-flex flex-column overflow-hidden vh-80 p-2 py-1"
        style={{ width: "92vw", maxWidth: "1180px" }}
      >

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between ev-padding-small border-bottom flex-shrink-0">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="ev-bold-med ev-reg-p-dark">
                {readOnly ? "Section Mapping" : "Review Section Mapping"}
              </span>
              {readOnly && (
                <span
                  className="ev-message-card6 ev-btn-xsmall fw-semibold avatar-tooltip-bottom"
                  data-tooltip="This is the mapping that was used to populate the preset's agents, it cannot be changed now"
                >
                  <i className="bi bi-lock-fill me-1"></i>Read only
                </span>
              )}
            </div>
            <div className="d-flex align-items-center flex-wrap gap-1">
              {docName && (
                <span className="ev-message-card1 ev-btn-xsmall me-1">
                  <i className="bi bi-file-earmark-text me-1"></i>{docName}
                </span>
              )}
              <i className="bi bi-arrow-left-right ev-reg-p-blue"></i>
              {libraryTemplateName && (
                <span className="ev-message-card1 ev-btn-xsmall ms-1">
                  <i className="bi bi-collection me-1"></i>{libraryTemplateName}
                </span>
              )}
            </div>
          </div>
          {activeDroppedCount > 0 && (
            <span
              className="ev-alert-card-danger text-danger avatar-tooltip avatar-tooltip-end"
              data-tooltip="Unmapped headings of document are highlighted in red. Their content will not be imported in the preset."
            >
              <i className="bi bi-exclamation-triangle me-1"></i>
              {activeDroppedCount} Heading(s) unmapped
            </span>
          )}
        </div>

        {/* Group navigation + filters */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 ev-padding-small py-2 border-bottom flex-shrink-0">
          <div className="position-relative me-3" style={{ width: "200px" }}>
              <input
                type="text"
                className="form-control search-input ms-0"
                placeholder="Search headings..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button type="button" className="ev-btn-lightblue-fill-round search-bar-button">
                <i className="bi bi-search"></i>
              </button>
            </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-small py-1"
              disabled={activeGroupIndex === 0}
              onClick={() => { setGroupIndex(activeGroupIndex - 1); setSelection(null); }}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            <div className="position-relative" ref={groupMenuRef} style={{ width: "300px" }}>
              <button
                type="button"
                className="formField7 d-flex align-items-center gap-2 text-start ev-fs-12 px-2 py-1"
                onClick={() => setIsGroupMenuOpen((prev) => !prev)}
              >
                <span className="text-truncate flex-grow-1">{groupLabel(activeGroup)}</span>
                <span className="ev-message-card6 ev-btn-xsmall border-0 ev-xsmall-p-blue flex-shrink-0">
                  {groupCountLabel(activeGroup)}
                </span>
                <i
                  className={`bi ${isGroupMenuOpen ? "bi-chevron-up" : "bi-chevron-down"} ev-xsmall-p-blue flex-shrink-0`}
                ></i>
              </button>

              {isGroupMenuOpen && (
                <div
                  className="ev-card-4 position-absolute top-100 mt-1 p-2 z-1000"
                  style={{ minWidth: "100%", width: "480px" }}
                >
                  <div className="scrollable-element vh-20 p-1">
                    {groups.map((group, index) => (
                      <div
                        key={group.key}
                        className={`ev-card-2 d-flex align-items-center justify-content-between gap-2 cursor-pointer mb-1 py-1 ${
                          index === activeGroupIndex ? "active" : ""
                        }`}
                        onClick={() => {
                          setGroupIndex(index);
                          setSelection(null);
                          setIsGroupMenuOpen(false);
                        }}
                      >
                        <span className="ev-small-p-dark text-truncate">{groupLabel(group)}</span>
                        <span className="ev-message-card6 ev-btn-xsmall border-0 ev-xsmall-p-blue flex-shrink-0">
                          {groupCountLabel(group)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-small py-1"
              disabled={activeGroupIndex >= groups.length - 1}
              onClick={() => { setGroupIndex(activeGroupIndex + 1); setSelection(null); }}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
            <span className="ev-xsmall-p-dark genbi-map-muted">
              Section {activeGroupIndex + 1} of {groups.length}
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            
            {[
              { key: "all", label: "All" },
              { key: "mapped", label: "Mapped" },
              { key: "unmapped", label: "Unmapped" },
            ].map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={`${filterMode === filter.key ? "ev-btn-blue-fill-round" : "ev-btn-blue-outline-round"} ev-btn-small`}
                onClick={() => setFilterMode(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/*
        {activeGroup && !activeGroup.isUnassigned && !activeGroup.isAll && (
          <div className="ev-padding-small py-2 flex-shrink-0">
            <div className="genbi-map-row genbi-map-groupbar px-2 py-2">
              <div style={{ minWidth: 0 }}>
                <p className="ev-xsmall-p-blue mb-0 genbi-map-muted">
                  Document heading {activeDocRoot?.section_ordering_id || "—"}
                </p>
                <p
                  className="ev-reg-p-blue ev-bold-reg mb-0 genbi-map-text"
                  title={activeDocRoot?.heading || ""}
                >
                  {activeDocRoot ? activeDocRoot.heading : "No matching heading"}
                </p>
              </div>
              <div className="genbi-map-connector">
                <span
                  className="avatar-tooltip"
                  data-tooltip="Top-level sections are matched automatically and cannot be changed"
                >
                  <i className="bi bi-arrow-left-right ev-reg-p-blue"></i>
                </span>
              </div>
              <div className="text-end" style={{ minWidth: 0 }}>
                <p className="ev-xsmall-p-blue mb-0 genbi-map-muted">
                  Preset section {activeGroup.rootRecord.order || "—"}
                </p>
                <p className="ev-reg-p-blue ev-bold-reg mb-0 genbi-map-text" title={activeGroup.rootRecord.name}>
                  {activeGroup.rootRecord.name}
                </p>
              </div>
            </div>
          </div>
        )}
        */}

        {activeGroup && activeGroup.isUnassigned && (
          <div className="ev-padding-small py-2 flex-shrink-0">
            <div className="ev-alert-card-warning py-2 px-3">
              <p className="mb-0 ev-xsmall-p-dark">
                <i className="bi bi-info-circle me-1"></i>
                This Preset has no section matching these top-level document headings.
                Their content, including all subheadings, will not be imported.
              </p>
            </div>
          </div>
        )}

        {/* Column headers */}
        <div className="genbi-map-row genbi-map-colhead ev-padding-small py-2 flex-shrink-0">
          <span className="ev-xsmall-p-dark genbi-map-muted">Document headings</span>
          <span></span>
          <div className="d-flex align-items-center justify-content-end gap-1">
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-xsmall ev-btn-border-square py-0 px-1"
              aria-label={allSectionsCollapsed ? "Expand all sections" : "Collapse all sections"}
              title={allSectionsCollapsed ? "Expand all sections" : "Collapse all sections"}
              disabled={collapsibleSectionKeys.length === 0}
              onClick={handleToggleAllSections}
            >
              <i className={`bi ${allSectionsCollapsed ? "bi-arrows-expand" : "bi-arrows-collapse"} me-1`}></i>
              <span className="d-none d-md-inline">
                {allSectionsCollapsed ? "Expand all" : "Collapse all"}
              </span>
            </button>
            <span className="ev-xsmall-p-dark genbi-map-muted">Preset sections</span>
          </div>
        </div>

        {/* Rows */}
        <DragDropContext onBeforeCapture={handleBeforeCapture} onDragEnd={handleDragEnd}>
          <div
            ref={scrollRef}
            className="scrollable-element genbi-map-scroll flex-grow-1 ev-padding-small py-1"
            style={{ minHeight: 0 }}
          >
            {visibleRows.length === 0 ? (
              <div className="ev-alert-card-warning text-center py-3">
                <p className="mb-0">
                  <i className="bi bi-info-circle me-1"></i>
                  {rows.length === 0
                    ? "This section has no subsections to review."
                    : "No headings match the current filter."}
                </p>
              </div>
            ) : (
              <>
                <Droppable droppableId="genbi-map-template-rows">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {renderedTemplateRows.map((row, index) => (
                          <Draggable
                            key={row.id}
                            draggableId={row.leftRecord.key}
                            index={index}
                            isDragDisabled={
                              !isReorderEnabled ||
                              row.isTopLevelListRow ||
                              row.leftRecord.key === editingKey
                            }
                          >
                            {(dragProvided, dragSnapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                className={`genbi-map-row${
                                  dragSnapshot.isDragging
                                    ? " genbi-map-dragging"
                                    : `${bandClassFor(row)}${selectionDimClass(row)}`
                                }`}
                                style={{
                                  ...clampDragPreview(
                                    dragProvided.draggableProps?.style,
                                    dragSnapshot,
                                    dragBoundsRef.current
                                  ),
                                  ...rowIndentStyle(row),
                                }}
                              >
                                {renderDocCell(row)}
                                {renderConnector(row)}
                                {renderTemplateCell(row, dragProvided)}
                                {expandedAgentsKey === row.leftRecord.key &&
                                  renderAgentsPanel(row.leftRecord)}
                              </div>
                            )}
                          </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>

                {!dragContext && docOnlyRows.map((row) => (
                  <div
                    key={row.id}
                    className={`genbi-map-row${selectionDimClass(row)}`}
                    style={rowIndentStyle(row)}
                  >
                    {renderDocCell(row)}
                    {renderConnector(row)}
                    {renderTemplateCell(row)}
                  </div>
                ))}
              </>
            )}
          </div>
        </DragDropContext>

        {/* Footer */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 ev-padding-small py-2 border-top flex-shrink-0">
          <div className="d-flex align-items-center gap-2">
            {/*<span className="ev-xsmall-p-dark genbi-map-muted">
              {sections.length} Preset sections
            </span> */}
            {undoAction && (
              <span className="d-flex align-items-center gap-2" role="status" aria-live="polite">
                <span className="ev-xsmall-p-dark">{undoAction.message}</span>
                <button
                  type="button"
                  className="ev-btn-blue-outline-round ev-btn-xsmall py-0"
                  onClick={handleUndo}
                >
                  Undo
                </button>
              </span>
            )}
            {selection && (
              <span className="ev-message-card6 ev-btn-xsmall border-0 ev-xsmall-p-blue ms-4">
                <i className="bi bi-cursor me-1"></i>
                Pick a heading on the other side, or press Esc
              </span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            {readOnly ? (
              <button type="button" className="ev-btn-blue-fill-round ev-bold-reg" onClick={onCancel}>
                Close
              </button>
            ) : (
              <>
                <button type="button" className="ev-btn-blue-outline-round ev-bold-reg" onClick={onCancel}>
                  Cancel
                </button>
                <button type="button" className="ev-btn-blue-fill-round ev-bold-reg" onClick={handleConfirm}>
                  Confirm &amp; Generate
                </button>
              </>
            )}
          </div>
        </div>

      </div>

      <DeleteConfirmationModal
        show={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDeleteSection}
        objectName="section"
        itemName={pendingDelete?.label || ""}
        bodyContent={
          <>
            {pendingDelete?.subsectionCount > 0 && (
              <p className="mb-1">
                Its {pendingDelete.subsectionCount} subsection
                {pendingDelete.subsectionCount === 1 ? "" : "s"} will be removed too.
              </p>
            )}
            {pendingDelete?.mappedCount > 0 && (
              <p className="mb-1">
                {pendingDelete.mappedCount} document heading
                {pendingDelete.mappedCount === 1 ? "" : "s"} mapped here will become
                unmapped, and that content will not be imported.
              </p>
            )}
            <p className="text-danger mb-0">This action cannot be undone.</p>
          </>
        }
      />
    </div>,
    document.body
  );
};

export default GenbiHeadingMappingModal;